import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { firestore } from '../../helpers/Firebase';

/**
 * Hook to fetch daily analytics data for a date range including previous period
 * @param {object} dateRange - Object with { startTimestamp, endTimestamp }
 * @param {string} spId - Optional service provider ID to filter
 */
export const useDailyAnalytics = (dateRange, spId = null) => {
  const [data, setData] = useState([]);
  const [previousData, setPreviousData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use ref to track previous values and prevent unnecessary fetches
  const prevValuesRef = useRef({ startTimestamp: null, endTimestamp: null, spId: null });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!dateRange || !dateRange.startTimestamp || !dateRange.endTimestamp) {
          setLoading(false);
          return;
        }

        const { startTimestamp, endTimestamp } = dateRange;
        
        // Skip fetch if values haven't actually changed (but allow initial load)
        const isInitialLoad = prevValuesRef.current.startTimestamp === null;
        const valuesChanged = 
          prevValuesRef.current.startTimestamp !== startTimestamp ||
          prevValuesRef.current.endTimestamp !== endTimestamp ||
          prevValuesRef.current.spId !== spId;
        
        if (!isInitialLoad && !valuesChanged) {
          return; // Skip fetch if values haven't changed
        }
        
        // Update ref with current values
        prevValuesRef.current = { startTimestamp, endTimestamp, spId };
        
        setLoading(true);

        // Calculate previous period (same length)
        const periodLength = endTimestamp - startTimestamp;
        const previousStartTimestamp = startTimestamp - periodLength;
        const previousEndTimestamp = startTimestamp;

        // Fetch both current and previous period in parallel
        const [currentSnapshot, previousSnapshot] = await Promise.all([
          // Current period
          firestore()
            .collection('analyticsDaily')
            .where('timestamp', '>=', startTimestamp)
            .where('timestamp', '<=', endTimestamp)
            .orderBy('timestamp', 'asc')
            .get(),
          // Previous period
          firestore()
            .collection('analyticsDaily')
            .where('timestamp', '>=', previousStartTimestamp)
            .where('timestamp', '<', previousEndTimestamp)
            .orderBy('timestamp', 'asc')
            .get()
        ]);

        // Process current period data
        const analyticsData = [];
        currentSnapshot.docs.forEach(doc => {
          const docData = doc.data();
          const dayData = {
            date: docData.date || moment(docData.timestamp).format('MMM DD'),
            timestamp: docData.timestamp,
            general: docData.general || {},
            SP: docData.SP || {},
            washer: docData.washer || {},
          };

          // If filtering by SP, extract that SP's data
          if (spId && docData.SP && docData.SP[spId]) {
            dayData.spData = docData.SP[spId];
          }

          analyticsData.push(dayData);
        });

        // Process previous period data
        const previousAnalyticsData = [];
        previousSnapshot.docs.forEach(doc => {
          const docData = doc.data();
          const dayData = {
            date: docData.date || moment(docData.timestamp).format('MMM DD'),
            timestamp: docData.timestamp,
            general: docData.general || {},
            SP: docData.SP || {},
            washer: docData.washer || {},
          };

          // If filtering by SP, extract that SP's data
          if (spId && docData.SP && docData.SP[spId]) {
            dayData.spData = docData.SP[spId];
          }

          previousAnalyticsData.push(dayData);
        });

        setData(analyticsData);
        setPreviousData(previousAnalyticsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching daily analytics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Only depend on the primitive values, not the object itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.startTimestamp, dateRange?.endTimestamp, spId]);

  return { data, previousData, loading, error };
};

/**
 * Component to aggregate daily analytics into chart-ready format
 */
export const prepareChartData = (analyticsData, metric = 'totalBookings', level = 'general') => {
  return analyticsData.map(day => {
    let value = 0;
    
    if (level === 'general') {
      // Handle metrics from counters (like canceled, rejected, etc.)
      if (metric === 'canceled' || metric === 'rejected' || metric === 'onlineBooking' || metric === 'offlineBooking') {
        value = day.general?.counters?.[metric] || 0;
      } else {
        value = day.general?.[metric] || 0;
      }
    } else if (level === 'SP' && day.spData) {
      // Handle metrics from counters for SP level
      if (metric === 'canceled' || metric === 'rejected' || metric === 'onlineBooking' || metric === 'offlineBooking') {
        value = day.spData?.counters?.[metric] || 0;
      } else {
        value = day.spData[metric] || 0;
      }
    }

    return {
      date: day.date,
      [metric]: value,
      timestamp: day.timestamp,
    };
  });
};

export default useDailyAnalytics;

